import { useState } from 'react'
import { ChatTeardrop, X, PaperPlaneTilt } from '@phosphor-icons/react'
import { submitFeedback } from '../api'

// Visible to every visitor, logged in or not — the no-signup tools
// (Should-I-Quit, WCIAR) are the highest-traffic surfaces, and gating this
// behind login made feedback unreachable exactly where most of it would
// come from. The backend rate-limits anonymous submissions (fail-closed).
export default function FeedbackButton() {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSend = async () => {
    if (!text.trim() || sending) return
    setSending(true)
    setError(null)
    try {
      await submitFeedback(text.trim())
      setSent(true)
      setText('')
      setTimeout(() => { setOpen(false); setSent(false) }, 2000)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Could not send. Try again.')
    } finally {
      setSending(false)
    }
  }

  return (
    <>
      {/* Pill */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Give feedback"
          className="fixed bottom-4 left-4 z-40 flex items-center gap-1.5 px-3 py-2 rounded-xl bg-surface border-2 border-edge shadow-brutal-sm font-bold text-xs text-ink-muted hover:text-ink hover:-translate-y-0.5 hover:shadow-brutal transition-all"
        >
          <ChatTeardrop size={14} weight="duotone" />
          <span className="hidden sm:inline">Feedback?</span>
        </button>
      )}

      {/* Modal */}
      {open && (
        <div className="fixed bottom-4 left-4 z-50 w-80 card-brutal bg-surface p-4 space-y-3 anim-pop">
          <div className="flex items-center justify-between">
            <p className="font-bold text-sm">What's on your mind?</p>
            <button onClick={() => setOpen(false)} aria-label="Close" className="p-1 rounded-lg hover:bg-muted transition-colors">
              <X size={14} weight="bold" className="text-ink-muted" />
            </button>
          </div>

          {sent ? (
            <p className="text-sm text-accent-ink font-bold py-4 text-center">Thanks! 🙏 Got it.</p>
          ) : (
            <>
              <textarea
                autoFocus
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && e.metaKey) handleSend() }}
                rows={4}
                placeholder="A bug, an idea, a complaint — anything goes."
                className="w-full text-sm rounded-xl border-2 border-edge bg-muted/40 p-3 resize-none focus:border-primary transition-colors placeholder:text-ink-muted"
              />
              {error && <p className="text-xs text-red-600 font-medium">{error}</p>}
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-ink-muted">⌘↵ to send</span>
                <button
                  onClick={handleSend}
                  disabled={!text.trim() || sending}
                  className="btn-primary text-sm py-1.5 px-3 inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PaperPlaneTilt size={14} weight="fill" />
                  {sending ? 'Sending…' : 'Send'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  )
}
